import React, { useMemo, useState, useRef, useCallback, useEffect, useContext } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

// --- New design-system primitives ---
import { FormShell, FormGrid, FormField, FormFooter } from '../../../../../../generic/ui/FormLayout'
import { AccentButton } from '../../../../../../generic/ui/Button'
import IconTextField from '../../../../../../generic/ui/Input/IconTextField'
import ProductSelector from '../../../../../../generic/ui/Selectors/ProductSelector'
import SizeSelector from '../../../../../../generic/ui/Selectors/SizeSelector'
import GrowerSelector from '../../../../../../generic/ui/Selectors/GrowerSelector'
import GrowerFieldSelector from '../../../../../../generic/ui/Selectors/GrowerFieldSelector'
import PackageSelector from '../../../../../../generic/ui/Selectors/PackageSelector'
import QualitySelector from '../../../../../../generic/ui/Selectors/QualitySelector'
import PalletTypeSelector from '../../../../../../generic/ui/Selectors/PalletTypeSelector'
import HashIcon from '../../../../../../generic/icons/IconLib/HashIcon'
import ScaleIcon from '../../../../../../generic/icons/IconLib/ScaleIcon'
import CoinIcon from '../../../../../../generic/icons/IconLib/CoinIcon'
import UserIcon from '../../../../../../generic/icons/IconLib/UserIcon'

// --- Existing pieces (logic intentionally untouched) ---
import { IReqProduct } from '../MarketCollectMain'
import { IProductsWithVarietyItem } from '../../../../../../modules/API/Defaults/models/compliteProducts'
import { IGetReuqestResponseNode } from '../../../../../../modules/API/Defaults/models/pallets'
import { IGrowerItem, IGrowerNodeGetResponse } from '../../../../../../modules/API/Defaults/models/growers'
import { IPackagesItem } from '../../../../../../modules/API/Defaults/models/packages'
import { IProductCashItem, IActionProductCash } from '../../../../../../../store/reducers/market'
import { RootState } from '../../../../../../../store/reducers/rootReducer'
import { ISizeItem } from '../../../../../../modules/API/Products/models/size'
import TypeCalcSelect from './generic/TypeCalcSelect'
import useDataFetcher from '../../../knisot/hooks/useFetchData'
import { Market } from '../../../../../../modules/API/Market/market'
import { IResponseSrikaNode, ISrikaItem } from '../../../../../../modules/API/Gathering/models/srika'
import useBarcodeScanner from '../hooks/useBarcodeScanner'
import { ContextModal } from '../../../../../../modules/modal/modalContext'
import { ISelectPalletProps } from '../types/selectedPallet'
import selectBrodMigdal from '../../../../../../generic/utils/selectBrodMigdal'
import { handleNotifications } from '../../../../../../generic/utils/handleNotifications'
import BarcodeScannerPanel from './BarcodeScannerPanel'
import { FormRoleFieldSettingsItemDto, GetFormRoleFieldSettingsResponseDto } from '../../../settings/FeatureControl/types'
import { IQualityItem } from '../../../../../../modules/API/Defaults/models/quality'
import { IGrowersAreaItem } from '../../../../../../modules/API/Defaults/models/growersArea'
import { Defaults } from '../../../../../../modules/API/Defaults/defaults'

type Props = {
  data: ISelectPalletProps
  dataGrowers: IGrowerNodeGetResponse
  dataPallets: IGetReuqestResponseNode
  packageList: IPackagesItem[]
  sellerID: string
  defaultPallet: number
  defaultPackage: number
  createProduct: (obj: IReqProduct) => void
  cashProduct: (data: IProductCashItem | null) => void
  dataProductsWithVariety: IProductsWithVarietyItem[]
  getSizes: (productID: number) => void
  sizeData: ISizeItem[]
  isWorker: boolean
  overweightDelta: number
  productCreationFields: GetFormRoleFieldSettingsResponseDto
  productCreationFieldsDict: Record<string, FormRoleFieldSettingsItemDto>
  qualityList: IQualityItem[]
}

const AddProductModal = ({
  data, dataGrowers, dataPallets, dataProductsWithVariety, getSizes,
  packageList, createProduct, defaultPallet, defaultPackage,
  cashProduct, sizeData, isWorker, overweightDelta,
  productCreationFieldsDict, qualityList,
}: Props) => {
  const [t] = useTranslation('teudot')
  const marketCash: IActionProductCash = useSelector((state: RootState) => state.marketReucer)
  const { pallet, client, seller } = data

  // ---------- Field-permission helpers (unchanged) ----------
  const getFieldConfig = (key: string) => productCreationFieldsDict[key]
  const isFieldVisible = (key: string) => !!getFieldConfig(key)?.canView
  const isFieldRequired = (key: string) =>
    getFieldConfig(key)?.isRequiredOverride ?? getFieldConfig(key)?.defaultIsRequired
  const isFieldEditable = (key: string) =>
    getFieldConfig(key)?.isEditableOverride ?? getFieldConfig(key)?.defaultIsEditable

  // ---------- Defaults / cached draft (unchanged) ----------
  const chashRef = useRef(marketCash.data ? marketCash.data[client] : null)
  const defaults = useSelector((state: RootState) => state.defaults.defaults)
  const isReqToTakePackages = selectBrodMigdal(defaults, 304, 'Logical_Value')
  const defaultGrower = selectBrodMigdal(defaults, 4, 'Num_Value')
  const defPack = packageList.find(
    (item) => item.PackID === (chashRef.current ? chashRef.current.packageID : defaultPackage),
  )
  const isEnabledCalcPallet = selectBrodMigdal(defaults, 651, 'Logical_Value')
  const isDisableTakeGoodsIfNotEnoughRest = selectBrodMigdal(defaults, 379, 'Logical_Value')

  const defPackQty = chashRef.current ? chashRef.current.packageQty : 0
  const defPrice = chashRef.current ? chashRef.current.price : 0
  const defWeight = chashRef.current ? chashRef.current.weight : 0
  const defReffernce = chashRef.current ? chashRef.current.reference : ''

  const withoutPalletID = useMemo(() => {
    return dataPallets.pallets.find((item) => item.Real_Pallete === 0)?.Pallete_Type || 0
  }, [dataPallets])

  const defGrower =
    dataGrowers.growers.find(
      (grower) =>
        grower.GrowerID === chashRef.current?.growerID || grower.GrowerID === defaultGrower,
    ) || null

  const defProduct =
    dataProductsWithVariety.find(
      (product) =>
        product.ProductID === chashRef.current?.productID &&
        product.VarietyID === chashRef.current?.varietyID,
    ) || null

  const [, setModal] = useContext(ContextModal)

  // ---------- Form state (unchanged) ----------
  const [selectedProduct, setSelectedProduct] = useState<IProductsWithVarietyItem | null>(defProduct)
  const [selectedGrower, setSelectedGrower] = useState<IGrowerItem | null>(defGrower)
  const [selectedPackage, setSelectedPackage] = useState<IPackagesItem | null>(defPack || null)
  const [packageQty, setPackageQty] = useState<number>(defPackQty)
  const [price, setPrice] = useState<number>(defPrice)
  const [remark, setRemark] = useState<string>('')
  const [reffernce] = useState<string | number>(defReffernce)
  const [referenceDeliveryEntry, setReferenceDeliveryEntry] = useState<number>(0)
  const [weightGross, setWeightGross] = useState<number>(defWeight)
  const [selectedPallete, setSelectedPallete] = useState<number>(
    pallet === 0 ? defaultPallet || withoutPalletID : withoutPalletID,
  )
  const [palletQty, setPalletQty] = useState<number>(1)
  const [selectedSize, setSelectedSize] = useState<number>(0)
  const [typeCalc, setTypeCalc] = useState<number>(0)
  const [isDefaultGrowerList, setIsDefaultGrowerList] = useState<boolean>(false)
  const [isNeedtoCalcPallet, setIsNeedtoCalcPallet] = useState<boolean>(isEnabledCalcPallet)
  const [entryData, setEntryData] = useState<IResponseSrikaNode | null>(null)
  const [isShowSecondaryPack, setIsShowSecondaryPack] = useState<boolean>(false)
  const [packIdSecondary, setPackIdSecondary] = useState<number>(0)
  const [quantityPackMain, setQuantityPackMain] = useState<number>(0)
  const [selectedQualityID, setSelectedQualityID] = useState<number>(0)
  const [selectedLotID, setSelectedLotID] = useState<number>(0)

  // Barcode scanner (unchanged)
  const { input, setInput, barcodeList, scanBarcode } = useBarcodeScanner(true)

  // Grower list fetched by product (unchanged)
  const { data: growerList, setData: setGrowerList } = useDataFetcher<IGrowerItem[]>(
    Market.getGrowerListByProductID,
    `${selectedProduct?.ProductID || 0}`,
  )

  // Grower-fields (lot/חלקה) fetched by grower (unchanged)
  const { data: growersAreaList } = useDataFetcher<IGrowersAreaItem[]>(
    Defaults.getGrowersAreaByGrowerID,
    `${selectedGrower?.GrowerID || 0}`,
  )

  useEffect(() => {
    if (!selectedProduct) return
    getSizes(selectedProduct.ProductID)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct])

  // Cache sync (unchanged)
  const updateCash = useCallback(cashProduct, [cashProduct])
  useMemo(() => {
    const tempCash: IProductCashItem = {
      growerID: selectedGrower?.GrowerID || 0,
      productID: selectedProduct?.ProductID || 0,
      varietyID: selectedProduct?.VarietyID || 0,
      packageID: selectedPackage?.PackID || 0,
      packageQty,
      price,
      weight: weightGross,
      reference: Number(reffernce),
    }
    updateCash(tempCash)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGrower, selectedProduct, selectedPackage, packageQty, price, weightGross, reffernce])

  // Overweight check (unchanged)
  useEffect(() => {
    if (!entryData || overweightDelta === 0) return
    const overWeight =
      (weightGross / (entryData.entryLine[0].AvgWeightPerPack * packageQty)) * 100 - 100
    if (Math.round(overWeight) > overweightDelta) {
      setModal({ open: true, mood: false, value: 'המשקל הממוצע שונה מכניסה' })
      setTimeout(() => {
        setModal({ open: false, mood: false, value: '' })
      }, 6000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageQty, weightGross, entryData, overweightDelta])

  // ---------- Handlers (unchanged behavior) ----------
  const handleSecondaryPackShow = () => setIsShowSecondaryPack(!isShowSecondaryPack)

  const handleSwitchGrowerList = () => {
    setIsDefaultGrowerList(!isDefaultGrowerList)
    setGrowerList(dataGrowers.growers)
  }

  const handleQuantityPackMain = (e: number) => {
    setQuantityPackMain(e)
    setWeightGross(e * packageQty)
  }

  const handleSelectPackage = (item: IPackagesItem | null) => {
    setSelectedPackage(item)
    if (item && item.Pack_Type === 'מ') {
      setPalletQty(0)
      setSelectedPallete(withoutPalletID)
    }
  }

  const handlePackageQty = (e: number) => {
    const qty = Math.round(Number(e))
    if (entryData) {
      const knisa = entryData.entryLine[0]
      if (isDisableTakeGoodsIfNotEnoughRest && knisa.BalanceAmount < qty) {
        handleNotifications('אין מספיק יתרות בכניסה', { type: 'error' })
      }
      if (qty === knisa.AmountPack) {
        setWeightGross(knisa.Gross)
        setIsNeedtoCalcPallet(true)
      }
      if (quantityPackMain !== 0) {
        setWeightGross(qty * quantityPackMain)
      }
    }
    setPackageQty(qty)
  }

  const handleSelectGrower = (item: IGrowerItem | null) => {
    setSelectedGrower(item)
    setSelectedLotID(0)
  }
  
  const handleSelectProduct = (item: IProductsWithVarietyItem | null) => {
    setSelectedProduct(item)
    if (!item) return
    handleSelectTypeCalc(item.CalculationMethod)
    setEntryData(null)
    if (selectedGrower?.GrowerID === defaultGrower) return
    setSelectedGrower(null)
    setSelectedLotID(0)
  }

  const handleSelectPallet = (palletType: number) => {
    if (+palletType === withoutPalletID) {
      setPalletQty(0)
    } else if (selectedPallete === withoutPalletID) {
      setPalletQty(1)
    }
    setSelectedPallete(+palletType)
  }

  const handleSelectSize = (size: number) => setSelectedSize(+size)
  const handlePalletQty = (e: number) => setPalletQty(e)
  const handlePrice = (e: number) => setPrice(Number(e))
  const handleRemark = (e: string) => setRemark(e)
  const handleReferenceDeliveryEntry = (e: string) => setReferenceDeliveryEntry(Number(e))
  const handleWeightGross = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWeightGross(Number(e.target.value))
  }

  // ---------- Validation (unchanged) ----------
  const isDisableSave = useMemo(() => {
    const hasNotEnoughRest =
      isDisableTakeGoodsIfNotEnoughRest &&
      !!entryData &&
      entryData.entryLine[0].BalanceAmount < packageQty

    let isPackageQtyInvalid = false
    if (isFieldVisible('PACKAGE_QTY') && isFieldRequired('PACKAGE_QTY')) {
      isPackageQtyInvalid = packageQty === 0
    } else {
      isPackageQtyInvalid = isReqToTakePackages ? packageQty === 0 : false
    }

    const isProductMissing = !selectedProduct
    const isGrowerMissing = !selectedGrower

    let isPackageMissing = false
    if (isFieldVisible('PACKAGE') && isFieldRequired('PACKAGE')) {
      isPackageMissing = !selectedPackage
    }

    let isTypeCalcInvalid = false
    if (isFieldVisible('TYPE_CALC') && isFieldRequired('TYPE_CALC')) {
      isTypeCalcInvalid = typeCalc === null || typeCalc === -1
    }

    let isSizeInvalid = false
    if (isFieldVisible('SIZE') && isFieldRequired('SIZE')) {
      const hasSelectedSize = !!selectedSize && sizeData?.some((item) => item.Code === selectedSize)
      isSizeInvalid = !hasSelectedSize
    }

    let isWeightInvalid = false
    if (isFieldVisible('WEIGHT_GROSS') && isFieldRequired('WEIGHT_GROSS')) {
      isWeightInvalid = weightGross === 0
    }

    let isPriceInvalid = false
    if (!isWorker && isFieldVisible('PRICE') && isFieldRequired('PRICE')) {
      isPriceInvalid = price === 0
    }

    let isPalletTypeInvalid = false
    if (pallet === 0 && isFieldVisible('PALLET_TYPE') && isFieldRequired('PALLET_TYPE')) {
      isPalletTypeInvalid = !selectedPallete
    }

    let isPalletQtyInvalid = false
    if (
      pallet === 0 &&
      isFieldVisible('PALLET_QTY') &&
      isFieldRequired('PALLET_QTY') &&
      withoutPalletID !== selectedPallete
    ) {
      isPalletQtyInvalid = palletQty <= 0
    }

    let isSecondaryPackInvalid = false
    if (isFieldVisible('PACK_SECONDARY') && isFieldRequired('PACK_SECONDARY')) {
      isSecondaryPackInvalid = packIdSecondary === 0
    }

    let isRemarkInvalid = false
    if (isFieldVisible('REMARK') && isFieldRequired('REMARK')) {
      isRemarkInvalid = !remark.trim().length
    }

    let isReferenceDeliveryEntryInvalid = false
    if (isFieldVisible('REFERENCE_DELIVERY_ENTRY') && isFieldRequired('REFERENCE_DELIVERY_ENTRY')) {
      isReferenceDeliveryEntryInvalid = referenceDeliveryEntry === 0
    }

    let isQualityInvalid = false
    if (isFieldVisible('QUALITY') && isFieldRequired('QUALITY')) {
      isQualityInvalid = selectedQualityID === 0
    }

    let isLotInvalid = false
    if (pallet === 0 && selectedGrower && isFieldVisible('GROWER_FIELD') && isFieldRequired('GROWER_FIELD')) {
      isLotInvalid = selectedLotID === 0
    }

    return (
      hasNotEnoughRest ||
      isPackageQtyInvalid ||
      isProductMissing ||
      isGrowerMissing ||
      isPackageMissing ||
      isTypeCalcInvalid ||
      isSizeInvalid ||
      isWeightInvalid ||
      isPriceInvalid ||
      isPalletTypeInvalid ||
      isPalletQtyInvalid ||
      isSecondaryPackInvalid ||
      isRemarkInvalid ||
      isReferenceDeliveryEntryInvalid ||
      isQualityInvalid ||
      isLotInvalid
    )
  }, [
    entryData,
    isDisableTakeGoodsIfNotEnoughRest,
    isReqToTakePackages,
    isWorker,
    packageQty,
    packIdSecondary,
    pallet,
    palletQty,
    price,
    productCreationFieldsDict,
    referenceDeliveryEntry,
    remark,
    selectedGrower,
    selectedLotID,
    selectedPackage,
    selectedPallete,
    selectedProduct,
    selectedQualityID,
    selectedSize,
    sizeData,
    typeCalc,
    weightGross,
    withoutPalletID,
  ])

  // ---------- Submit (unchanged) ----------
  const handleCreateProduct = () => {
    if (isDisableSave) return
    const palletWeight =
      dataPallets.pallets.find((item) => item.Pallete_Type === selectedPallete)?.Tare_Weight || 0
    const finalWeight = !isNeedtoCalcPallet ? weightGross : weightGross - palletWeight * palletQty
    try {
      createProduct({
        selectedProduct: selectedProduct,
        GrowerID: selectedGrower?.GrowerID || 0,
        PalletNum: pallet === 0 ? 999999 : pallet,
        Qty: packageQty,
        PalletQty: palletQty,
        Weight: finalWeight,
        PalletType: selectedPallete || 0,
        CustomerID: client,
        PackID: selectedPackage?.PackID || 0,
        Price: price,
        Type_SaleCalculation: typeCalc || 0,
        Remarks: remark,
        EntryNum: input,
        PackID_Secondary: packIdSecondary,
        Quantity_Packs_Main: quantityPackMain,
        SizeID: sizeData.find((item) => item.Code === selectedSize)?.Description.trim() || '',
        CustomerSeller: `${client}-${seller}`,
        Reference_Delivery_Entry: referenceDeliveryEntry,
        QualityID: selectedQualityID,
        LotID: selectedLotID,
      })
      cashProduct(null)
    } catch (error) {
      /* noop — createProduct shows its own error */
    }
  }

  const handleSelectTypeCalc = useCallback((type: number) => setTypeCalc(type), [])

  // ---------- Barcode scan integration (unchanged) ----------
  const srikaCalculation = (item: ISrikaItem, res: IResponseSrikaNode) => {
    handleSelectProduct({
      ProductID: item.ProductID,
      VarietyID: item.VarietyID,
      ProductDesc: item.ProductDesc,
      VarietyDesc: item.VarietyDesc,
      GeneralProductID: 0,
      GroupID: [],
      CalculationMethod: item.ProductCalculationMethod,
    })
    handleSelectTypeCalc(item.ProductCalculationMethod)
    handleSelectGrower({
      GrowerID: item.GrowerID,
      GrowerName: item.GrowerName,
      AccounID: 'string',
      Active: true,
      ClientID: 0,
      Exporter_Num: 0,
      Farm_Num: 0,
      FieldMan: 0,
      GGN: 'string',
      GroupID: 0,
      Kanat_Num: 0,
      Muaza_Num: 'string',
      Pay_Discharge: true,
      Pay_Levies: true,
      Pay_Logistics: true,
      Payment_Method: 0,
      PickUp: true,
    })
    handleSelectSize(item.SizeNum)
    setEntryData(res)

    const secodarySum =
      !item.Quantity || !item.Quantity_Packs_Main ? 0 : item.Quantity * item.Quantity_Packs_Main
    handlePackageQty(item.Quantity || 0)
    setWeightGross(secodarySum)
    setPackIdSecondary(item.PackID_Secondary || 0)
    setQuantityPackMain(item.Quantity_Packs_Main || 0)
    handleSelectPallet(pallet === 0 ? item.PalletID || selectedPallete : withoutPalletID)

    const pack = packageList.filter((p) => p.PackID === item.PackID)[0]
    if (!pack) return
    handleSelectPackage(pack)
  }

  const handleScanSuccess = (data: ISrikaItem, fullResponse: IResponseSrikaNode) => {
    srikaCalculation(data, fullResponse)
  }

  const handleScanBarcode = async (barcode?: string) => {
    try {
      await scanBarcode(handleScanSuccess, barcode)
    } catch (error) {
      setModal({ open: true, mood: false, value: 'לא נמצא פריט עם קוד זה' })
      setTimeout(() => {
        setModal({ open: false, mood: false, value: '' })
      }, 6000)
    }
  }

  const handleSelectBarcode = (item: ISrikaItem) => {
    srikaCalculation(item, { entryLine: [item] })
  }

  const calculateWeightByAvgKnisaWeigh = () => {
    if (!entryData) return
    const knisa = entryData.entryLine[0]
    const avgWeight = knisa.AvgWeightPerPack
    setWeightGross(+(avgWeight * packageQty).toFixed(2))
  }

  // ---------- Derived selector values (Selectors take full objects) ----------
  const sizeValue = useMemo(
    () => sizeData?.find((s) => s.Code === selectedSize) ?? null,
    [sizeData, selectedSize],
  )
  const palleteValue = useMemo(
    () => dataPallets.pallets.find((p) => p.Pallete_Type === selectedPallete) ?? null,
    [dataPallets, selectedPallete],
  )
  const qualityValue = useMemo(
    () => qualityList?.find((q) => q.QualityID === selectedQualityID) ?? null,
    [qualityList, selectedQualityID],
  )
  const lotValue = useMemo(
    () => (growersAreaList || []).find((f) => f.LotID === selectedLotID) ?? null,
    [growersAreaList, selectedLotID],
  )
  const secondaryPackValue = useMemo(
    () => packageList.find((p) => p.PackID === packIdSecondary) ?? null,
    [packageList, packIdSecondary],
  )

  // ---------- Render ----------
  return (
    <FormShell>
      {isFieldVisible('BARCODE_ENTRY') && (
        <BarcodeScannerPanel
          input={input}
          setInput={setInput}
          onScan={handleScanBarcode}
          onSelectFromList={handleSelectBarcode}
          barcodeList={barcodeList?.entryLine}
          autoFocus
          disabled={!isFieldEditable('BARCODE_ENTRY')}
        />
      )}

      {entryData && entryData.entryLine[0].BalanceAmount !== 9999 && (
        <BalanceText $isMarked={entryData.entryLine[0].BalanceAmount <= 0}>
          {entryData.entryLine[0].BalanceAmount} יתרה בכניסה
        </BalanceText>
      )}

      <FormGrid>
        {isFieldVisible('PRODUCT_VARIETY') && (
          <FormField label={t('create_product_modal_product')} required={isFieldRequired('PRODUCT_VARIETY')}>
            <ProductSelector
              products={dataProductsWithVariety}
              value={selectedProduct}
              onSelect={handleSelectProduct}
              disabled={!!entryData || !isFieldEditable('PRODUCT_VARIETY')}
            />
          </FormField>
        )}

        {isFieldVisible('SIZE') && selectedProduct && (
          <FormField label={t('create_product_modal_size')} required={isFieldRequired('SIZE')}>
            <SizeSelector
              sizes={sizeData || []}
              value={sizeValue}
              onSelect={(s) => handleSelectSize(s ? s.Code : 0)}
              disabled={!!entryData || !isFieldEditable('SIZE')}
            />
          </FormField>
        )}

        {selectedProduct && isFieldVisible('GROWER') && (
          <FormField
            label={t('create_product_modal_grower')}
            required={isFieldRequired('GROWER')}
            fullWidth
          >
            <GrowerRow>
              <GrowerSelectorSlot>
                <GrowerSelector
                  growers={growerList || []}
                  value={selectedGrower}
                  onSelect={handleSelectGrower}
                  disabled={!!entryData || !isFieldEditable('GROWER')}
                />
              </GrowerSelectorSlot>
              <ToggleChip
                type='button'
                onClick={handleSwitchGrowerList}
                $active={isDefaultGrowerList}
                aria-label={t('create_product_modal_grower_button')}
                title={t('create_product_modal_grower_button')}
              >
                <UserIcon />
              </ToggleChip>
            </GrowerRow>
          </FormField>
        )}

        {(isFieldVisible('PACKAGE_QTY') || isFieldVisible('PACKAGE')) && (
          <PairField>
            {isFieldVisible('PACKAGE') && (
              <FormField
                label={t('create_product_modal_grower_package')}
                required={isFieldRequired('PACKAGE')}
              >
                <PackageSelector
                  packages={packageList}
                  value={selectedPackage}
                  onSelect={handleSelectPackage}
                  disabled={!!entryData && !!selectedPackage}
                />
              </FormField>
            )}
            {isFieldVisible('PACKAGE_QTY') && (
              <FormField label={t('create_product_modal_grower_qty')} required={isFieldRequired('PACKAGE_QTY')}>
                <IconTextField
                  type='number'
                  inputMode='numeric'
                  leadingIcon={<HashIcon />}
                  value={packageQty}
                  onChange={(e) => handlePackageQty(Number(e.target.value))}
                  disabled={!isFieldEditable('PACKAGE_QTY')}
                  onWheel={(e) => (e.currentTarget as HTMLInputElement).blur()}
                />
              </FormField>
            )}
          </PairField>
        )}

        {(() => {
          const showLot =
            isFieldVisible('GROWER_FIELD') &&
            !!selectedGrower &&
            !!selectedProduct &&
            pallet === 0
          const showQuality = isFieldVisible('QUALITY')
          if (!showLot && !showQuality) return null
          return (
            <PairField>
              {showQuality && (
                <FormField label='איכות' required={isFieldRequired('QUALITY')}>
                  <QualitySelector
                    qualities={qualityList || []}
                    value={qualityValue}
                    onSelect={(q) => setSelectedQualityID(q ? q.QualityID : 0)}
                    disabled={!isFieldEditable('QUALITY')}
                  />
                </FormField>
              )}
              {showLot && (
                <FormField label='חלקה' required={isFieldRequired('GROWER_FIELD')}>
                  <GrowerFieldSelector
                    fields={growersAreaList || []}
                    value={lotValue}
                    onSelect={(f) => setSelectedLotID(f ? f.LotID : 0)}
                    disabled={!isFieldEditable('GROWER_FIELD')}
                  />
                </FormField>
              )}
            </PairField>
          )
        })()}

        {isFieldVisible('PACK_SECONDARY') && (
          <FormField fullWidth>
            <SecondaryToggle
              type='button'
              onClick={handleSecondaryPackShow}
              $open={isShowSecondaryPack}
            >
              <span>אריזה משנית</span>
              <Caret $open={isShowSecondaryPack}>▾</Caret>
            </SecondaryToggle>
            {isShowSecondaryPack && (
              <SecondaryGrid>
                <PackageSelector
                  packages={packageList}
                  value={secondaryPackValue}
                  onSelect={(p) => setPackIdSecondary(p ? p.PackID : 0)}
                  placeholder='חפש אריזת משנה'
                  disabled={!isFieldEditable('PACK_SECONDARY')}
                />
                <IconTextField
                  type='number'
                  inputMode='numeric'
                  leadingIcon={<HashIcon />}
                  value={quantityPackMain}
                  onChange={(e) => handleQuantityPackMain(Number(e.target.value))}
                  placeholder='כמות באריזה'
                />
              </SecondaryGrid>
            )}
          </FormField>
        )}

        {((selectedProduct && isFieldVisible('TYPE_CALC')) ||
          isFieldVisible('REFERENCE_DELIVERY_ENTRY')) && (
          <PairField>
            {selectedProduct && isFieldVisible('TYPE_CALC') && (
              <FormField label={t('create_product_modal_calcMethod')} required={isFieldRequired('TYPE_CALC')}>
                <TypeCalcSelect
                  handleSelect={handleSelectTypeCalc}
                  defaultType={typeCalc}
                  disabled={!isFieldEditable('TYPE_CALC')}
                />
              </FormField>
            )}
            {isFieldVisible('REFERENCE_DELIVERY_ENTRY') && (
              <FormField label='אסמכתה' required={isFieldRequired('REFERENCE_DELIVERY_ENTRY')}>
                <IconTextField
                  type='number'
                  inputMode='numeric'
                  leadingIcon={<HashIcon />}
                  value={referenceDeliveryEntry}
                  onChange={(e) => handleReferenceDeliveryEntry(e.target.value)}
                  placeholder='אסמכתה..'
                  onWheel={(e) => (e.currentTarget as HTMLInputElement).blur()}
                  disabled={!isFieldEditable('REFERENCE_DELIVERY_ENTRY')}
                />
              </FormField>
            )}
          </PairField>
        )}

        {(isFieldVisible('WEIGHT_GROSS') || (!isWorker && isFieldVisible('PRICE'))) && (
          <PairField>
            {isFieldVisible('WEIGHT_GROSS') && (
              <FormField label={t('create_product_modal_weight')} required={isFieldRequired('WEIGHT_GROSS')}>
                <IconTextField
                  type='number'
                  leadingIcon={<ScaleIcon />}
                  endAdornment={
                    entryData ? (
                      <ScaleBtn type='button' onClick={calculateWeightByAvgKnisaWeigh} aria-label='avg-weight'>
                        <ScaleIcon />
                      </ScaleBtn>
                    ) : undefined
                  }
                  value={weightGross}
                  onChange={handleWeightGross}
                  min={-100}
                  onWheel={(e) => (e.currentTarget as HTMLInputElement).blur()}
                  disabled={!isFieldEditable('WEIGHT_GROSS')}
                />
              </FormField>
            )}
            {!isWorker && isFieldVisible('PRICE') && (
              <FormField label={t('create_product_modal_price')} required={isFieldRequired('PRICE')}>
                <IconTextField
                  type='number'
                  leadingIcon={<CoinIcon />}
                  defaultValue={price}
                  onChange={(e) => handlePrice(Number(e.target.value))}
                  disabled={!isFieldEditable('PRICE')}
                  onWheel={(e) => (e.currentTarget as HTMLInputElement).blur()}
                />
              </FormField>
            )}
          </PairField>
        )}

        {pallet === 0 && isFieldVisible('PALLET_TYPE') && (
          <>
            <PairField>
              <FormField label={t('create_product_modal_pallet')} required={isFieldRequired('PALLET_TYPE')}>
                <PalletTypeSelector
                  palletTypes={dataPallets.pallets}
                  value={palleteValue}
                  onSelect={(p) => handleSelectPallet(p ? p.Pallete_Type : 0)}
                  disabled={!isFieldEditable('PALLET_TYPE')}
                />
              </FormField>
              {withoutPalletID !== selectedPallete && (
                <FormField label={t('create_product_modal_grower_qty')} required={isFieldRequired('PALLET_QTY')}>
                  <IconTextField
                    type='number'
                    inputMode='numeric'
                    leadingIcon={<HashIcon />}
                    value={palletQty}
                    onChange={(e) => handlePalletQty(Number(e.target.value))}
                    disabled={!isFieldEditable('PALLET_QTY')}
                    onWheel={(e) => (e.currentTarget as HTMLInputElement).blur()}
                  />
                </FormField>
              )}
            </PairField>
            {isFieldVisible('INCLUDE_TARE_WEIGHT') && (
              <FormField fullWidth>
                <CheckRow
                  type='button'
                  onClick={() => {
                    if (!isFieldEditable('INCLUDE_TARE_WEIGHT')) return
                    setIsNeedtoCalcPallet(!isNeedtoCalcPallet)
                  }}
                  disabled={!isFieldEditable('INCLUDE_TARE_WEIGHT')}
                  aria-pressed={isNeedtoCalcPallet}
                >
                  <CheckBoxBox $checked={isNeedtoCalcPallet} aria-hidden>
                    {isNeedtoCalcPallet && <CheckGlyph />}
                  </CheckBoxBox>
                  <CheckLabel>{t('create_product_modal_includeTareWeight')}</CheckLabel>
                </CheckRow>
              </FormField>
            )}
          </>
        )}

        {isFieldVisible('REMARK') && (
          <FormField label={t('create_product_modal_remark')} required={isFieldRequired('REMARK')} fullWidth>
            <RemarkArea
              placeholder={t('create_product_modal_remark')}
              value={remark}
              onChange={(e) => handleRemark(e.target.value)}
              required={isFieldRequired('REMARK')}
              disabled={!isFieldEditable('REMARK')}
            />
          </FormField>
        )}
      </FormGrid>

      <FormFooter>
        <AccentButton
          type='button'
          disabled={isDisableSave}
          onClick={handleCreateProduct}
          fullWidth={false}
        >
          {t('create_product_modal_button_save')}
        </AccentButton>
      </FormFooter>
    </FormShell>
  )
}

export default AddProductModal

// ---------- Styled (themed) ----------

const PairField = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1rem 1.25rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 0.625rem 0.5rem;
  }

  & > *:only-child {
    grid-column: 1 / -1;
  }
`

const BalanceText = styled.p<{ $isMarked?: boolean }>`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.sm};
  color: ${({ theme, $isMarked }) => ($isMarked ? theme.colors.danger : theme.colors.ink2)};
  text-align: end;
`

const GrowerRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: stretch;
  gap: 0.5rem;
`

const GrowerSelectorSlot = styled.div`
  min-width: 0;
`

const ToggleChip = styled.button<{ $active?: boolean }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.875rem;
  height: 2.875rem;
  border-radius: ${({ theme }) => theme.radii.md};
  background: ${({ theme, $active }) => ($active ? theme.colors.accent : theme.colors.surface)};
  color: ${({ theme }) => theme.colors.ink2};
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.accent : theme.colors.inputBorder)};
  cursor: pointer;
  transition: all ${({ theme }) => theme.motion.fast} ease;

  svg {
    width: 1rem; height: 1rem;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};
  }
`

const SecondaryToggle = styled.button<{ $open?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.625rem;
  background: ${({ theme, $open }) => ($open ? theme.colors.accentSoft : theme.colors.line2)};
  border: 1px solid ${({ theme, $open }) => ($open ? theme.colors.accent : theme.colors.line)};
  border-radius: ${({ theme }) => theme.radii.sm};
  color: ${({ theme }) => theme.colors.ink2};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.sm};
  cursor: pointer;
  transition: all ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`

const Caret = styled.span<{ $open?: boolean }>`
  display: inline-block;
  transition: transform ${({ theme }) => theme.motion.fast} ease;
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
`

const SecondaryGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 0.5rem;
  margin-top: 0.5rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: minmax(0, 1fr);
  }
`

const ScaleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.ink2};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radii.sm};
  transition: background ${({ theme }) => theme.motion.fast} ease;

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.accentSoft};
    color: ${({ theme }) => theme.colors.ink};
  }
`

const CheckGlyph = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth={3}
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden
  >
    <polyline points='20 6 9 17 4 12' />
  </svg>
)

const CheckRow = styled.button`
  appearance: none;
  width: 100%;
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.625rem;
  border: 1px solid ${({ theme }) => theme.colors.line};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radii.md};
  cursor: pointer;
  text-align: start;
  direction: rtl;
  transition: all ${({ theme }) => theme.motion.fast} ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.accent};
    background: ${({ theme }) => theme.colors.accentSoft};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

const CheckBoxBox = styled.span<{ $checked?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.radii.sm};
  border: 1px solid
    ${({ theme, $checked }) => ($checked ? theme.colors.accent : theme.colors.inputBorder)};
  background: ${({ theme, $checked }) =>
    $checked ? theme.colors.accent : theme.colors.surface};
  color: ${({ theme }) => theme.colors.ink};
  transition: all ${({ theme }) => theme.motion.fast} ease;

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`

const CheckLabel = styled.span`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.md};
  color: ${({ theme }) => theme.colors.ink};
`

const RemarkArea = styled.textarea`
  width: 100%;
  min-height: 4.5rem;
  resize: vertical;
  padding: 0.625rem 0.875rem;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.md};
  color: ${({ theme }) => theme.colors.ink};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.inputBorder};
  border-radius: ${({ theme }) => theme.radii.md};
  outline: none;
  direction: rtl;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.ink4};
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.inputBorderHover};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.ink};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.line2};
    cursor: not-allowed;
    opacity: 0.7;
  }
`
